/**
 * D-GTCC-SHELL1 — the 17 Mono-Deck nav icons, copied 1:1 (viewBox, stroke
 * attributes, path data) from GTCC_REFERENCE_2026-09-01.html's `.it .ic` markup.
 * Kept local to `gtcc/` (not `packages/design-system`) because this package's
 * scope is D-GTCC-SHELL1's touched-files list; a shared-icon-module migration
 * is left to whichever package B-GTCC-UI1 lands its shared tokens in.
 */
import type { ReactElement, SVGProps } from "react";

export type GtccIconKey =
  | "ovw"
  | "alin"
  | "risk"
  | "launch"
  | "cockpit"
  | "diag"
  | "dplane"
  | "aia"
  | "ten"
  | "hw"
  | "gw"
  | "mail"
  | "noti"
  | "audit"
  | "pset"
  | "sec"
  | "dz";

function svg(children: SVGProps<SVGSVGElement>["children"]) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable={false}
    >
      {children}
    </svg>
  );
}

const GTCC_ICON_RENDERERS: Readonly<Record<GtccIconKey, () => ReactElement>> = {
  ovw: () =>
    svg(
      <>
        <circle cx="12" cy="12" r="8.5" />
        <circle cx="12" cy="12" r="3" fill="currentColor" stroke="none" />
      </>,
    ),
  alin: () =>
    svg(
      <>
        <path d="M12 3.5 21 19.5H3Z" />
        <line x1="12" y1="10" x2="12" y2="14" />
        <circle cx="12" cy="16.8" r=".9" fill="currentColor" stroke="none" />
      </>,
    ),
  risk: () =>
    svg(
      <>
        <circle cx="12" cy="12" r="8.5" />
        <circle cx="12" cy="12" r="4.5" />
        <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
      </>,
    ),
  launch: () =>
    svg(
      <>
        <path d="M5 19 12 5l7 14" />
        <line x1="8.5" y1="13" x2="15.5" y2="13" />
      </>,
    ),
  cockpit: () =>
    svg(
      <>
        <line x1="5" y1="19" x2="5" y2="11" />
        <line x1="10" y1="19" x2="10" y2="5" />
        <line x1="15" y1="19" x2="15" y2="9" />
        <line x1="20" y1="19" x2="20" y2="13" />
      </>,
    ),
  diag: () => svg(<path d="M3 12h4l2-5 3 10 2-5h7" />),
  dplane: () =>
    svg(
      <>
        <ellipse cx="12" cy="6" rx="7" ry="2.6" />
        <path d="M5 6v6c0 1.4 3.1 2.6 7 2.6s7-1.2 7-2.6V6" />
        <path d="M5 12v6c0 1.4 3.1 2.6 7 2.6s7-1.2 7-2.6v-6" />
      </>,
    ),
  aia: () =>
    svg(
      <>
        <circle cx="12" cy="12" r="2.2" />
        <path d="M12 4.5v3M12 16.5v3M4.5 12h3M16.5 12h3M6.7 6.7l2.1 2.1M15.2 15.2l2.1 2.1M17.3 6.7l-2.1 2.1M8.8 15.2l-2.1 2.1" />
      </>,
    ),
  ten: () =>
    svg(
      <>
        <rect x="5" y="4" width="9" height="16" />
        <line x1="8" y1="8" x2="11" y2="8" />
        <line x1="8" y1="12" x2="11" y2="12" />
        <path d="M14 9h5v11H5" />
      </>,
    ),
  hw: () =>
    svg(
      <>
        <circle cx="12" cy="13" r="2" />
        <path d="M7.5 8.5a6.4 6.4 0 0 1 9 0" />
        <path d="M4.8 5.8a10.2 10.2 0 0 1 14.4 0" />
        <line x1="12" y1="15" x2="12" y2="20" />
      </>,
    ),
  gw: () =>
    svg(
      <>
        <path d="M4 8h13l-3-3" />
        <path d="M20 16H7l3 3" />
      </>,
    ),
  mail: () =>
    svg(
      <>
        <rect x="3.5" y="5.5" width="17" height="13" rx="1.5" />
        <path d="m4 7 8 6 8-6" />
      </>,
    ),
  noti: () =>
    svg(
      <>
        <path d="M6 16v-5a6 6 0 0 1 12 0v5l1.5 2.5H4.5Z" />
        <path d="M10.3 21a2 2 0 0 0 3.4 0" />
      </>,
    ),
  audit: () =>
    svg(
      <>
        <line x1="5" y1="7" x2="19" y2="7" />
        <line x1="5" y1="12" x2="19" y2="12" />
        <line x1="5" y1="17" x2="13" y2="17" />
      </>,
    ),
  pset: () =>
    svg(
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M12 4v2.2M12 17.8V20M20 12h-2.2M6.2 12H4M17.7 6.3l-1.6 1.6M7.9 16.1l-1.6 1.6M17.7 17.7l-1.6-1.6M7.9 7.9 6.3 6.3" />
      </>,
    ),
  sec: () => svg(<path d="M12 3.5 19 6v6c0 4.5-3 7.5-7 8.5-4-1-7-4-7-8.5V6Z" />),
  dz: () =>
    svg(
      <>
        <path d="M12 3.5 19 6v6c0 4.5-3 7.5-7 8.5-4-1-7-4-7-8.5V6Z" />
        <line x1="9.5" y1="9.5" x2="14.5" y2="14.5" />
        <line x1="14.5" y1="9.5" x2="9.5" y2="14.5" />
      </>,
    ),
};

export function GtccIcon({ iconKey }: { readonly iconKey: GtccIconKey }): ReactElement {
  const render = GTCC_ICON_RENDERERS[iconKey];
  return render();
}

export function isGtccIconKey(key: string): key is GtccIconKey {
  return Object.prototype.hasOwnProperty.call(GTCC_ICON_RENDERERS, key);
}
